import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { CommonModule, NgOptimizedImage, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';

import { environment } from '../../../../environments/environment';

Swiper.use([Navigation, Pagination, Autoplay]);

@Component({
  selector: 'app-common-swiper',
  standalone: true,
  imports: [CommonModule, NgOptimizedImage],
  templateUrl: './common-swiper.component.html',
  styleUrls: ['./common-swiper.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class CommonSwiperComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  drImageBaseUrl = environment.baseUrl + '/MediaUploads/UserUpload/';
  selectedIndex = 0;

  @Input() data: any[] = [];
  @Input() type = 'default';
  @Output() expertClick = new EventEmitter<any>();

  @ViewChild('swiperRef') swiperRef!: ElementRef<HTMLElement>;

  private readonly isBrowser: boolean;
  private viewInitialized = false;
  private hasInitializedSwiper = false;
  private swiperInstance?: Swiper;
  private intersectionObserver?: IntersectionObserver;
  private initFrameId?: number;
  private idleCallbackId?: number;

  constructor(
    private router: Router,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.queueSwiperInitialization();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewInitialized || this.hasInitializedSwiper) {
      return;
    }

    if (changes['data']?.currentValue?.length) {
      this.queueSwiperInitialization();
    }
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.swiperInstance?.destroy(true, true);

    if (!this.isBrowser) {
      return;
    }

    const browserWindow = window as Window & {
      cancelIdleCallback?: (handle: number) => void;
    };

    if (this.initFrameId !== undefined) {
      browserWindow.cancelAnimationFrame(this.initFrameId);
    }

    if (
      this.idleCallbackId !== undefined &&
      typeof browserWindow.cancelIdleCallback === 'function'
    ) {
      browserWindow.cancelIdleCallback(this.idleCallbackId);
    }
  }

  private queueSwiperInitialization(): void {
    if (
      !this.isBrowser ||
      this.hasInitializedSwiper ||
      !this.data?.length ||
      !this.swiperRef?.nativeElement
    ) {
      return;
    }

    const swiperElement = this.swiperRef.nativeElement;

    if (!('IntersectionObserver' in window)) {
      this.scheduleSwiperInitialization();
      return;
    }

    if (this.intersectionObserver) {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        this.intersectionObserver?.disconnect();
        this.intersectionObserver = undefined;
        this.scheduleSwiperInitialization();
      },
      { rootMargin: '200px 0px' }
    );

    this.intersectionObserver.observe(swiperElement);
  }

  private scheduleSwiperInitialization(): void {
    if (this.initFrameId !== undefined || this.hasInitializedSwiper) {
      return;
    }

    const browserWindow = window as Window & {
      requestIdleCallback?: (
        callback: IdleRequestCallback,
        options?: IdleRequestOptions
      ) => number;
    };

    this.initFrameId = browserWindow.requestAnimationFrame(() => {
      this.initFrameId = undefined;

      const initialize = () => {
        this.idleCallbackId = undefined;

        if (this.hasInitializedSwiper || !this.swiperRef?.nativeElement) {
          return;
        }

        this.ngZone.runOutsideAngular(() => {
          this.swiperInstance = new Swiper(
            this.swiperRef.nativeElement,
            this.getConfig()
          );
          this.hasInitializedSwiper = true;
        });
      };

      if (typeof browserWindow.requestIdleCallback === 'function') {
        this.idleCallbackId = browserWindow.requestIdleCallback(
          () => initialize(),
          { timeout: 500 }
        );
        return;
      }

      initialize();
    });
  }

  getConfig() {
    const baseConfig: any = {
      loop: true,
      spaceBetween: 20,
      watchOverflow: true,
      autoplay: {
        delay: 3500,
        disableOnInteraction: false
      }
    };

    const configs: any = {
      benefits: {
        slidesPerView: 3
      },
      depressionslide: {
        slidesPerView: 3
      },
      ocdTherapies: {
        slidesPerView: 2
      },
      addictionSlides: {
        slidesPerView: 2
      },
      angerSymptoms: {
        slidesPerView: 2
      },
      ppdSymptoms: {
        slidesPerView: 2
      },
      ppdslides: {
        slidesPerView: 2
      },
      sleepCauses: {
        slidesPerView: 2
      },
      slides: {
        slidesPerView: 3
      },
      therapy: {
        slidesPerView: 2
      },
      expert: {
        slidesPerView: 3,
        navigation: true
      },
      package: {
        slidesPerView: 3
      },
      review: {
        slidesPerView: 3
      },
      service: {
        slidesPerView: 3
      },
      couples: {
        slidesPerView: 3
      },
      ourApproach: {
        slidesPerView: 2
      },
      upcomingappointment: {
        slidesPerView: 1
      }
    };

    return {
      ...baseConfig,
      ...configs[this.type],
      pagination: {
        el: '.swiper-pagination',
        clickable: true
      },
      navigation: configs[this.type]?.navigation
        ? {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev'
          }
        : false,
      breakpoints: {
        320: { slidesPerView: 1 },
        768: { slidesPerView: 2 },
        1024: { slidesPerView: configs[this.type]?.slidesPerView || 3 }
      }
    };
  }

  bookPackage(data: any) {
    console.log(data);

    sessionStorage.setItem('isLogin', 'true');
    sessionStorage.setItem('packageDetails', JSON.stringify(data));

    this.router.navigate(['/book-package']);
  }

  onExpertClick(item: any) {
    this.expertClick.emit(item);
  }
}
